import * as React from 'react';
import { Box, Container, Typography, Grid, Link, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const benefits = [
    {
        icon: CheckCircleOutlineIcon,
        title: 'Eliminate Recurring Quality Issues',
        description: 'Break the cycle of repeated problems with AI-powered root cause analysis',
        color: 'cyan'
    },
    {
        icon: TrendingUpIcon,
        title: "Improve Customer's Satisfaction",
        description: 'Deliver consistent quality that exceeds customer expectations',
        color: 'blue'
    },
    {
        icon: EmojiEventsIcon,
        title: 'Enhance Supplier Credibility',
        description: 'Build trust through transparent and effective quality management',
        color: 'cyan'
    },
    {
        icon: AccessTimeIcon,
        title: 'Save Time & Cost of Poor Quality',
        description: 'Reduce waste, rework, and operational costs significantly',
        color: 'blue'
    },
    {
        icon: VerifiedUserIcon,
        title: 'Compliance Ready',
        description: 'Meet industry standards and regulatory requirements effortlessly',
        color: 'cyan'
    },
    {
        icon: PeopleOutlineIcon,
        title: 'Upskill Your Team',
        description: 'Empower your workforce with AI-guided quality management expertise',
        color: 'blue'
    }
];

const BenefitCard = ({ icon: Icon, title, description, color, theme }) => {
    const cyanGradient = 'linear-gradient(to right bottom, #22D3EE, #06B6D4)';
    const blueGradient = 'linear-gradient(to right bottom, #60A5FA, #3B82F6)';
    const gradient = color === 'cyan' ? cyanGradient : blueGradient;
    const hoverColor = color === 'cyan' ? '#d7e9ecff' : '#dbe2eeff';

    return (
        <Box
            className="group"
            sx={{
                backgroundColor: 'white',
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                boxSizing: 'border-box',
                border: '1px solid',
                borderColor: theme.palette.grey[100],
                transition: 'all 0.3s',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                    boxShadow: theme.shadows[10],
                    background: hoverColor,
                    '& .icon-wrapper': {
                        transform: 'scale(1.05)',
                        boxShadow: theme.shadows[6],
                    }
                },
            }}
        >
            <Box
                className="icon-wrapper"
                sx={{
                    width: 56,
                    height: 56,
                    background: gradient,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    color: 'white',
                    transition: 'all 0.3s',
                }}
            >
                <Icon sx={{ width: 32, height: 32 }} />
            </Box>
            <Typography variant="h7" sx={{ fontWeight: 600, color: theme.palette.grey[900], mb: 2 }}>
                {title}
            </Typography>
            <Typography sx={{ color: theme.palette.grey[600], fontSize: '0.75rem', mb: 2 }}>
                {description}
            </Typography>
        </Box>
    );
};

export default function Benefits() {
    const theme = useTheme();

    return (
        <Box
            component="section"
            id="benefits"
            sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(to bottom right, ${theme.palette.grey[50]}, white, #ECFEFF)`,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 384,
                    height: 384,
                    backgroundColor: '#22D3EE',
                    borderRadius: '50%',
                    filter: 'blur(48px)',
                    opacity: 0.2,
                    transform: 'translate(50%, -50%)',
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: 256,
                    height: 256,
                    backgroundColor: '#60A5FA',
                    borderRadius: '50%',
                    filter: 'blur(48px)',
                    opacity: 0.2,
                    transform: 'translate(-50%, 50%)',
                    zIndex: 0,
                }}
            />

            <Container
                maxWidth="lg"
                sx={{
                    px: { xs: 2, sm: 3 },
                    position: 'relative',
                    zIndex: 10,
                }}
            >
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box sx={{ display: 'inline-block', mb: 2 }}>
                        <Typography
                            component="span"
                            sx={{
                                background: 'linear-gradient(to right, #3B82F6, #06B6D4)',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1.5,
                                boxShadow: theme.shadows[3],
                            }}
                        >
                            The NC Pro Advantage
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            color: theme.palette.grey[900],
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        Transform Your Quality
                        <br />
                        <Box component="span" sx={{
                            background: 'linear-gradient(to right, #3B82F6, #06B6D4)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}>
                            From Reactive to Predictive
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: theme.palette.grey[600],
                        }}
                    >
                        NC Pro delivers tangible results that impact your bottom line and your customer relationships.
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 3, md: 4 }}
                    sx={{ width: '100%', mb: { xs: 8, md: 12 } }}
                >
                    {benefits.map((benefit, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <BenefitCard {...benefit} theme={theme} />
                        </Grid>
                    ))}
                </Grid>

                <Box
                    sx={{
                        background: 'linear-gradient(to right, #217eb4ff, #509de6ff)',
                        borderRadius: 3,
                        p: { xs: 4, md: 6 },
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 384,
                            height: 384,
                            backgroundColor: '#22D3EE',
                            borderRadius: '50%',
                            filter: 'blur(48px)',
                            opacity: 0.2,
                            transform: 'translate(50%, -50%)',
                            zIndex: 0,
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: 256,
                            height: 256,
                            backgroundColor: '#76aceeff',
                            borderRadius: '50%',
                            filter: 'blur(48px)',
                            opacity: 0.2,
                            transform: 'translate(-50%, 50%)',
                            zIndex: 0,
                        }}
                    />

                    <Box sx={{ position: 'relative', zIndex: 10, textAlign: 'center', maxWidth: 640, mx: 'auto' }}>
                        <Typography
                            component="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 700,
                                mb: 3,
                                lineHeight: 1.2,
                            }}
                        >
                            Ready to Experience the Difference?
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '1.0rem',
                                color: '#CFFAFE',
                                mb: 4,
                            }}
                        >

                            Join leading companies that trust NC Pro for their quality management needs
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                justifyContent: 'center',
                            }}
                        >
                            <Link
                                href="#demo"
                                underline="none"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                    backgroundColor: 'white',
                                    color: '#1E3A8A',
                                    px: 4,
                                    py: 2,
                                    borderRadius: 3,
                                    fontWeight: 600,
                                    fontSize: '1.125rem',
                                    transition: 'all 0.3s',
                                    boxShadow: theme.shadows[6],
                                    '&:hover': {
                                        backgroundColor: '#ECFEFF',
                                        boxShadow: theme.shadows[10],
                                        transform: 'scale(1.02)',
                                    },
                                }}
                            >
                                <PlayArrowIcon sx={{ width: 20, height: 20 }} />
                                Request a Demo
                            </Link> <Link
                                href="https://www.ncpro.gbinternational.sg"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(4px)',
                                    color: 'white',
                                    px: 4,
                                    py: 2,
                                    borderRadius: 3,
                                    fontWeight: 600,
                                    fontSize: '1.125rem',
                                    transition: 'all 0.3s',
                                    boxShadow: theme.shadows[6],
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        transform: 'scale(1.02)',
                                    },
                                }}
                            >
                                Learn More on Website
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
